/**
 * Client-side calculation for inside post-to-post measurements
 * Matches the backend TrussCalculationService logic exactly
 *
 * Backend reference: MyTruss.Application.Services.TrussCalculationService
 */

import { normalizeBuildingPostSize, WidthMeasurementReference } from './building-constants';

export type PostSize = '6x6' | '8x8' | '10x10';
export type ProductType = 'Gable' | 'Header' | 'LeanTo';

const POST_WIDTH_FULL: Record<PostSize, number> = {
  '6x6': 11, // 5.5" per side × 2 = 11"
  '8x8': 15, // 7.5" per side × 2 = 15"
  '10x10': 19, // 9.5" per side × 2 = 19"
};

const POST_WIDTH_HALF: Record<PostSize, number> = {
  '6x6': 5.5,
  '8x8': 7.5,
  '10x10': 9.5,
};

/**
 * Rounds measurement to nearest quarter inch (0.25")
 */
function roundToQuarterInch(inches: number): number {
  const quarterInch = 0.25;
  return Math.round(inches / quarterInch) * quarterInch;
}

/** Recommended minimum inside post-to-post measurement (1 foot). Below this is advisory only. */
export const MIN_INSIDE_POST_MEASUREMENT_INCHES = 12;

export const LIKELY_TOO_SMALL_INSIDE_POST_MESSAGE =
  'This inside post-to-post measurement is likely too small. Verify the truss span and post size.';

/** True when measurement is positive but below the 12" recommended minimum. */
export function isLikelyTooSmallInsidePostMeasurement(measurementInches: number): boolean {
  return measurementInches > 0 && measurementInches < MIN_INSIDE_POST_MEASUREMENT_INCHES;
}

/**
 * Resolves building vs Lean-To override post size for inside-post calc (UDOMI-1029).
 */
export function resolveEffectivePostSize(
  buildingPostSize: string | undefined | null,
  postSizeOverride?: boolean | null,
  postSizeOverrideValue?: string | null
): PostSize | null {
  const rawSource =
    postSizeOverride && postSizeOverrideValue ? postSizeOverrideValue : buildingPostSize;
  const raw = normalizeBuildingPostSize(rawSource) ?? rawSource?.trim();
  if (!raw) {
    return null;
  }
  if (raw === '6x6' || raw === '8x8' || raw === '10x10') {
    return raw;
  }
  // Enum-style values from API (SixBySix / EightByEight / TenByTen)
  const enumToDisplay: Record<string, PostSize> = {
    SixBySix: '6x6',
    EightByEight: '8x8',
    TenByTen: '10x10',
  };
  if (raw in enumToDisplay) {
    return enumToDisplay[raw];
  }
  return null;
}

/**
 * Validates measurement is within acceptable range
 */
function validateMeasurement(
  calculatedInches: number,
  sizeFeet: number,
  sizeInches: number
): boolean {
  const trussSize = sizeFeet * 12 + sizeInches;

  if (calculatedInches <= 0) {
    return false;
  }

  if (calculatedInches >= trussSize) {
    return false;
  }

  return true;
}

/**
 * Calculates inside post-to-post measurement for a truss
 * @param sizeFeet Truss size in feet
 * @param sizeInches Truss size in inches (0-11.5)
 * @param postSize Building post size (6x6, 8x8, 10x10)
 * @param productType Truss type (Gable, Header, LeanTo)
 * @param monoslopeTruss For Lean-To only: use full width (true) or half width (false)
 * @returns Calculated inside measurement in decimal inches, or 0 if invalid
 */
export function calculateInsidePostMeasurement(
  sizeFeet: number,
  sizeInches: number,
  postSize: PostSize,
  productType: ProductType,
  monoslopeTruss: boolean = false
): number {
  const totalInches = sizeFeet * 12 + sizeInches;

  let postWidthAdjustment: number;
  if (productType === 'Gable' || productType === 'Header') {
    postWidthAdjustment = POST_WIDTH_FULL[postSize];
  } else if (productType === 'LeanTo') {
    postWidthAdjustment = monoslopeTruss ? POST_WIDTH_FULL[postSize] : POST_WIDTH_HALF[postSize];
  } else {
    return 0;
  }

  const calculatedInches = totalInches - postWidthAdjustment;

  if (!validateMeasurement(calculatedInches, sizeFeet, sizeInches)) {
    return 0;
  }

  return roundToQuarterInch(calculatedInches);
}

/** Normalizes truss span inches for calculation (API may omit the field). */
export function normalizeTrussSizeInches(sizeInches: number | null | undefined): number {
  return sizeInches ?? 0;
}

/** Uses stored feet, or the in-progress input when the user is editing the size field. */
export function resolveEffectiveSizeFeet(
  sizeFeet: number | null | undefined,
  sizeFeetInput?: string
): number {
  if (sizeFeet != null && sizeFeet > 0) {
    return sizeFeet;
  }

  if (sizeFeetInput != null) {
    const trimmed = sizeFeetInput.trim();
    if (trimmed !== '' && trimmed !== '-') {
      const parsed = parseInt(trimmed, 10);
      if (!isNaN(parsed) && parsed > 0) {
        return parsed;
      }
    }
  }

  return sizeFeet ?? 0;
}

/** Recalculates inside-post measurement from truss dimensions (used before save validation). */
export function resolveInsidePostMeasurementForTruss(
  sizeFeet: number,
  sizeInches: number | null | undefined,
  productType: ProductType,
  buildingPostSize: string | null | undefined,
  options?: {
    monoslopeTruss?: boolean;
    postSizeOverride?: boolean | null;
    postSizeOverrideValue?: string | null;
  }
): number {
  const normalizedInches = normalizeTrussSizeInches(sizeInches);

  if (sizeFeet == null || sizeFeet <= 0) {
    return 0;
  }

  const postSize = resolveEffectivePostSize(
    buildingPostSize,
    options?.postSizeOverride,
    options?.postSizeOverrideValue
  );
  if (!postSize) {
    return 0;
  }

  return calculateInsidePostMeasurement(
    sizeFeet,
    normalizedInches,
    postSize,
    productType,
    options?.monoslopeTruss ?? false
  );
}

/**
 * Calculates actual width based on measurement reference
 * @param widthFeet Building width in feet
 * @param widthInches Building width in inches (0-11.5)
 * @param postSize Building post size (6x6, 8x8, 10x10)
 * @param productType Truss type (Gable, Header, LeanTo)
 * @param monoslopeTruss For Lean-To only: use full width (true) or half width (false)
 * @param measurementReference How width is measured (InsideInside, OutsideOutside, SurfaceInside, SurfaceOutside)
 * @returns Calculated actual width in decimal inches
 */
export function calculateActualWidth(
  widthFeet: number,
  widthInches: number,
  postSize: PostSize,
  productType: ProductType,
  monoslopeTruss: boolean,
  measurementReference: WidthMeasurementReference
): number {
  const widthAsEntered = widthFeet * 12 + widthInches;

  // Outside-Outside or Surface-Outside: no adjustment
  if (
    measurementReference === WidthMeasurementReference.OutsideOutside ||
    measurementReference === WidthMeasurementReference.SurfaceOutside
  ) {
    return widthAsEntered;
  }

  // Inside-Inside or Surface-Inside: add post width
  let postWidthAdjustment: number;
  if (productType === 'Gable' || productType === 'Header') {
    postWidthAdjustment = POST_WIDTH_FULL[postSize];
  } else if (productType === 'LeanTo') {
    postWidthAdjustment = monoslopeTruss ? POST_WIDTH_FULL[postSize] : POST_WIDTH_HALF[postSize];
  } else {
    return widthAsEntered;
  }

  return widthAsEntered + postWidthAdjustment;
}
